import React, { useState } from 'react';
import { AdaptiveAlertDialog } from '@/components/ui/AdaptiveAlertDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DeleteAccountDialogProps {
  isOpen: boolean;
  isDeleting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmDelete: () => void;
  email?: string;
}

const DeleteAccountDialog = ({ 
  isOpen, 
  isDeleting, 
  onOpenChange, 
  onConfirmDelete,
  email 
}: DeleteAccountDialogProps) => {
  const [confirmationText, setConfirmationText] = useState('');
  const isConfirmationValid = confirmationText === 'DELETE';

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setConfirmationText('');
    }
    onOpenChange(open);
  };

  const handleConfirm = () => {
    if (isConfirmationValid) {
      onConfirmDelete();
    }
  };

  const getDescription = () => (
    <div className="space-y-4">
      <p>
        This will permanently delete your account ({email}) and all associated data. 
        This action cannot be undone.
      </p>
      
      <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
        <p className="text-sm font-medium text-destructive">⚠️ Warning</p>
        <p className="text-sm text-muted-foreground mt-1">
          All your recipes, meal plans, shopping lists, and other data will be permanently deleted.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmation">
          Type <strong>DELETE</strong> to confirm:
        </Label>
        <Input
          id="confirmation"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder="Type DELETE here"
        />
      </div>
    </div>
  );

  return (
    <AdaptiveAlertDialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      title="Delete Account Permanently"
      description={getDescription()}
      cancelText="Cancel"
      confirmText={isDeleting ? 'Deleting...' : 'Delete Account'}
      onConfirm={handleConfirm}
      isLoading={isDeleting || !isConfirmationValid}
      isDestructive={true}
    />
  );
};

export default DeleteAccountDialog;